import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { removeToken, saveToken } from '../../../store/actions/token.actions';
import { selectToken } from '../../../store/selectors/token.selectors';
import { Store } from '@ngrx/store';
import { logout } from '../../../store/actions/auth.actions';
import { clearPermissions } from '../../../store/actions/permission.actions';
import { ROUTES } from '../../../shared/constants';
import { ISignInRes } from '../../../shared/models/auth.model';
import { AppInitializationService } from '../app-initialization.service';
import { SignalRService } from '../singal-r.service';

/* =====================
   Payload interfaces
   ===================== */

interface LoginPayload {
  email: string;
  password: string;
}

interface ResetPasswordPayload {
  email: string;
  newPassword: string;
}

interface OtpPayload {
  email?: string;
  otpCode: number;
}
interface OtpResetPayload {
  email?: string;
  code: number;
}

interface MessageResponse {
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = `${environment.baseUrl}/identity/api/Auth/`;
  readonly refreshTokenEndPoint = `${this.baseUrl}refresh`;

  constructor(
    private readonly _http: HttpClient,
    private readonly _store: Store,
    private readonly _router: Router,
    private readonly _injector: Injector,
    private readonly _appInitService: AppInitializationService,
  ) { }

  /* =====================
     Auth APIs
     ===================== */
     sendVerificationCode(token: string): Observable<MessageResponse> {
      return this._http.post<MessageResponse>(
        `${this.baseUrl}verify-email`,
        { token: token }
      );
    }

  login(credentials: LoginPayload): Observable<MessageResponse> {
    return this._http.post<MessageResponse>(
      `${this.baseUrl}login`,
      credentials,
      { withCredentials: true }
    );
  }

  verifyLoginOtp(payload: OtpPayload): Observable<ISignInRes> {
    return this._http.post<ISignInRes>(
      `${this.baseUrl}login/verify`,
      payload,
      { withCredentials: true }
    );
  }

  forgotPassword(data: { email: string }): Observable<MessageResponse> {
    return this._http.post<MessageResponse>(
      `${this.baseUrl}forget-password`,
      data,
      { withCredentials: true }
    );
  }

  verifyResetOtp(payload: OtpResetPayload): Observable<MessageResponse> {
    return this._http.post<MessageResponse>(
      `${this.baseUrl}reset/verify`,
      payload,
      { withCredentials: true }
    );
  }

  resetPassword(payload: ResetPasswordPayload): Observable<MessageResponse> {
    return this._http.post<MessageResponse>(
      `${this.baseUrl}reset/confirm`,
      payload,
      { withCredentials: true }
    );
  }

  resendActivationLink(data: { email: string }): Observable<MessageResponse> {
    return this._http.post<MessageResponse>(
      `${this.baseUrl}resend-verification`,
      data
    );
  }

  refreshToken(): Observable<MessageResponse> {
    return this._http.post<MessageResponse>(
      this.refreshTokenEndPoint,
      {},
      { withCredentials: true }
    ).pipe(
      catchError(error =>
        throwError(() => ({
          message: error?.error?.message || error?.message || 'Token refresh failed'
        }))
      )
    );
  }

  /* =====================
     Token handling
     ===================== */

  saveToken(token: string): void {
    if (token) {
      this._store.dispatch(saveToken({ token }));
    }
  }

  removeToken(): void {
    this._store.dispatch(removeToken());
  }

  async getToken(): Promise<string | null> {
    return firstValueFrom(
      this._store.select(selectToken).pipe(take(1))
    );
  }

  /* =====================
     Logout
     ===================== */

  logout(): void {
    this._http
      .post<void>(`${this.baseUrl}logout`, {}, { withCredentials: true })
      .pipe(take(1))
      .subscribe({
        complete: () => this.cleanupAfterLogout(),
        error: () => this.cleanupAfterLogout()
      });
  }

  private cleanupAfterLogout(): void {
    this._appInitService.reset();
    this._store.dispatch(logout());
    this._store.dispatch(clearPermissions());
    this._router.navigate([ROUTES.SIGN_IN]);
  }
}
